import { BaseTariffPage } from "@/components/tarif/BaseTariffPage";
import { Shield, Phone, CheckCircle2, Clock, FileText, Users, Building2, Stethoscope, Hospital, AlertCircle, TrendingUp, Award, Zap } from "lucide-react";

const HDI_COLOR = "#008A44";

const HdiTarif = () => {
  const heroFeatures = [
    { icon: Zap, title: "Auto-Updates", subtitle: "Kostenlos" },
    { icon: CheckCircle2, title: "Keine Erhöhung", subtitle: "Nach Schaden" },
    { icon: Shield, title: "Cloud-Schutz", subtitle: "Inklusive" }
  ];

  const coverageAreas = [
    {
      icon: Shield,
      title: "Eigenschäden",
      items: [
        "IT-Forensik und Schadensanalyse",
        "Datenwiederherstellung nach Angriffen",
        "Betriebsunterbrechung & Umsatzausfall",
        "System- und Softwarewiederherstellung",
        "Cyber-Erpressung (Ransomware)"
      ]
    },
    {
      icon: AlertCircle,
      title: "Drittschäden (Haftpflicht)",
      items: [
        "DSGVO-Bußgelder und Schadensersatz",
        "Datenschutzverletzungen",
        "Ansprüche Dritter bei Datenlecks",
        "Abwehr unberechtigter Forderungen",
        "Rechtsanwalts- und Gerichtskosten"
      ]
    },
    {
      icon: Phone,
      title: "24/7 Assistance & Krisenmanagement",
      items: [
        "Cyber-Notfall-Hotline rund um die Uhr",
        "Persönlicher Schadenmanager",
        "Koordination aller Dienstleister",
        "Krisenkommunikation & PR-Beratung",
        "Psychologische Erstbetreuung"
      ]
    },
    {
      icon: TrendingUp,
      title: "Prävention & Cloud-Schutz",
      items: [
        "Kostenlose Sicherheitsberatung",
        "Cloud-basierte Praxissoftware versichert",
        "Homeoffice-Arbeitsplätze mitversichert",
        "Externe Rechenzentren abgedeckt",
        "Mitversicherung von Tochterfirmen"
      ]
    }
  ];

  const targetGroups = [
    {
      icon: Stethoscope,
      title: "Hausarzt- & Facharztpraxen",
      description: "Einzelpraxen und kleine Gemeinschaftspraxen mit hoher Patientenfrequenz",
      benefits: [
        "Schutz vor Ransomware-Angriffen auf Praxissoftware",
        "DSGVO-Absicherung bei Patientendatenverarbeitung",
        "24/7-Notfall-Hotline für schnelle Hilfe",
        "Keine Tariferhöhung nach Schadensfall"
      ],
      recommendation: "Empfohlene Deckung: 500.000 - 1.000.000 €"
    },
    {
      icon: Users,
      title: "MVZ & Gemeinschaftspraxen",
      description: "Mehrere Standorte mit vernetzten IT-Systemen und Cloud-Anbindung",
      benefits: [
        "Alle Standorte unter einem Vertrag versichert",
        "Cloud- und Homeoffice-Arbeitsplätze mitversichert",
        "Höhere Deckungssummen für komplexe Schadensfälle",
        "Automatische Leistungsupdates ohne Mehrkosten"
      ],
      recommendation: "Empfohlene Deckung: 1.000.000 - 3.000.000 €"
    },
    {
      icon: Building2,
      title: "Zahnarztpraxen & Dentallabore",
      description: "Hochdigitalisierte Praxen mit bildgebenden Systemen und Cloud-Speicher",
      benefits: [
        "Schutz teurer digitaler Diagnose- und Bildgebungsgeräte",
        "Cloud-Speicher für Röntgenbilder mitversichert",
        "DSGVO-Schutz bei Datenübermittlung an Labore",
        "Betriebsunterbrechung bei Systemausfällen"
      ],
      recommendation: "Empfohlene Deckung: 750.000 - 2.000.000 €"
    },
    {
      icon: Hospital,
      title: "Kliniken & Krankenhäuser",
      description: "Große medizinische Einrichtungen mit kritischer IT-Infrastruktur",
      benefits: [
        "Maximalschutz für lebenserhaltende IT-Systeme",
        "Mitversicherung von Tochtergesellschaften",
        "Deckung von Massendatenverlusten",
        "Spezialisiertes Krisenmanagement für Healthcare"
      ],
      recommendation: "Empfohlene Deckung: 3.000.000 - 10.000.000 €"
    }
  ];

  const insuranceConditions = [
    {
      icon: Shield,
      title: "Selbstbehalt",
      description: "Flexibler Selbstbehalt ab 500 € pro Schadensfall. Höhere Selbstbehalte reduzieren die Prämie. Optional auch Vollkasko ohne Selbstbehalt möglich."
    },
    {
      icon: CheckCircle2,
      title: "Deckungssummen",
      description: "Von 250.000 € bis 10.000.000 € wählbar. Individuelle Anpassung an Praxisgröße und Risikoprofil. Ausreichend hohe Summen für alle medizinischen Einrichtungen."
    },
    {
      icon: Phone,
      title: "Service & Support",
      description: "24/7 Cyber-Notfall-Hotline mit Direktdurchwahl. Persönlicher Schadenmanager koordiniert alle Dienstleister. Kostenlose Präventionsberatung inklusive."
    },
    {
      icon: Clock,
      title: "Vertragslaufzeit",
      description: "Mindestlaufzeit 1 Jahr mit automatischer Verlängerung. Flexible Kündigungsfristen. Sonderkündigungsrecht nach reguliertem Schadensfall."
    },
    {
      icon: FileText,
      title: "Kündigungsfrist",
      description: "3 Monate zum Vertragsende. Automatische Verlängerung um ein Jahr bei Nichtkündigung. Nach Schadensfall beidseitiges Sonderkündigungsrecht innerhalb eines Monats."
    },
    {
      icon: Award,
      title: "Besondere Vorteile",
      description: "Automatische Leistungsupdates ohne Mehrkosten. Keine Tariferhöhung nach Schadensfall. Mitversicherung von Tochterfirmen und Cloud-Diensten."
    }
  ];

  const faqs = [
    {
      question: "Was macht die HDI Cyberversicherung für medizinische Einrichtungen besonders?",
      answer: "HDI bietet automatische Leistungsupdates ohne Mehrkosten, Mitversicherung von Tochterfirmen und garantiert keine Tariferhöhung nach Schadensfällen. Die Police wird kontinuierlich an neue Cyber-Bedrohungen angepasst, ohne dass Sie tätig werden müssen."
    },
    {
      question: "Sind Cloud-Dienste und Homeoffice-Arbeitsplätze mitversichert?",
      answer: "Ja, die HDI Cyberversicherung deckt explizit Cloud-basierte Praxissoftware, externe Rechenzentren und Homeoffice-Arbeitsplätze Ihrer Mitarbeiter mit ab - ein wichtiger Punkt für moderne, digital arbeitende Praxen."
    },
    {
      question: "Wie funktioniert das Schadenmanagement bei HDI?",
      answer: "Nach Schadenmeldung über die 24/7-Hotline erfolgt sofortige Schadensaufnahme. HDI koordiniert IT-Forensiker, Rechtsanwälte und Datenrettungsexperten. Sie erhalten einen persönlichen Schadenmanager als zentralen Ansprechpartner für den gesamten Prozess."
    },
    {
      question: "Welche Deckungssummen sind für meine Praxis sinnvoll?",
      answer: "Einzelpraxen: 500.000 - 1.000.000 €, Gemeinschaftspraxen/MVZ: 1.000.000 - 3.000.000 €, Kliniken: 3.000.000 - 10.000.000 €. Die optimale Höhe hängt von Patientenzahl, Digitalisierungsgrad und Umsatz ab."
    },
    {
      question: "Gibt es eine Wartezeit nach Vertragsabschluss?",
      answer: "Nein, der Versicherungsschutz beginnt sofort nach Vertragsbeginn ohne Wartezeit. Bereits ab dem ersten Tag sind Sie vollständig gegen Cyber-Risiken abgesichert."
    },
    {
      question: "Was passiert mit meinem Beitrag nach einem Schadensfall?",
      answer: "HDI garantiert: Keine Tariferhöhung nach Schadensfällen. Ihr Beitrag bleibt stabil, auch wenn Sie die Versicherung in Anspruch nehmen - ein einzigartiger Vorteil im Markt."
    },
    {
      question: "Werden auch DSGVO-Bußgelder übernommen?",
      answer: "Ja, die HDI Cyberversicherung deckt DSGVO-Bußgelder der Datenschutzbehörden vollständig ab. Zusätzlich sind Rechtsberatung, Behördenmeldungen und Betroffeneninformation versichert."
    }
  ];

  const documents = [
    {
      title: "HDI Cyberversicherung Produktinformation",
      type: "PDF, ca. 900 KB",
      url: "/erstinformation.pdf"
    },
    {
      title: "Versicherungsbedingungen (AVB)",
      type: "PDF, ca. 1,5 MB",
      url: "/erstinformation.pdf"
    }
  ];

  return (
    <BaseTariffPage
      companyName="HDI"
      companyColor={HDI_COLOR}
      productRating={{ value: 4.7, count: 445 }}
      priceRange="60-225"
      pageTitle="HDI Cyberversicherung für Ärzte & Praxen 2025 | Automatische Updates ohne Mehrkosten"
      metaDescription="HDI Cyberversicherung mit automatischen Leistungsupdates, Cloud-Schutz & Garantie: Keine Tariferhöhung nach Schadensfall. Tochterfirmen mitversichert. Jetzt beraten lassen!"
      metaKeywords="HDI Cyberversicherung, Cyber-Versicherung Ärzte, HDI Praxis Cyberschutz, Cloud-Schutz medizin, Leistungsupdate Cyberversicherung"
      canonicalUrl="https://praxis-versichert.de/tarifwissen/hdi"
      heroLabel="Automatische Leistungsupdates ohne Mehrkosten"
      heroTitle="HDI Cyberversicherung für medizinische Einrichtungen"
      heroDescription="Zukunftssicherer Cyber-Schutz mit automatischen Leistungsupdates, Cloud-Absicherung und der Garantie: Keine Tariferhöhung nach Schadensfällen. Inklusive Mitversicherung von Tochterfirmen und Homeoffice-Arbeitsplätzen."
      heroFeatures={heroFeatures}
      alertTitle="Warum HDI?"
      alertDescription="HDI bietet automatische Leistungsupdates ohne Mehrkosten und garantiert: Keine Tariferhöhung nach Schadensfällen. Cloud-basierte Systeme, Homeoffice-Arbeitsplätze und Tochterfirmen sind automatisch mitversichert – perfekt für moderne, digital arbeitende Praxen."
      coverageAreas={coverageAreas}
      targetGroups={targetGroups}
      insuranceConditions={insuranceConditions}
      faqs={faqs}
      documents={documents}
    />
  );
};

export default HdiTarif;
